<script setup>
import { onMounted, onUnmounted, watch, getCurrentInstance } from "vue";
import { useRoute, useRouter } from "vue-router";
import { storeToRefs } from "pinia";
import ThreePanePage from "@/templates/ThreePanePage.vue";
import { useResultsV2Store } from "@stores/survey/resultsV2/resultsV2";
import { useResultsV2CardsStore } from "@stores/survey/resultsV2/resultsV2.cards";
import { useSurveyStore } from "@stores/survey/survey";
import { usePrintDashboard } from "@/composables/usePrintDashboard";
import { useAssistantPageState } from "@/composables/useAssistantPageState";
import { usePopulationLabels } from "@/composables/usePopulationLabels";
import { useResultsV2PopulationsStore } from "@stores/survey/resultsV2/resultsV2.populations";
import ResultsV2Sidebar from "@/components/Survey/ResultsV2/ResultsV2Sidebar.vue";
import ResultsV2ViewSidebar from "@/components/Survey/ResultsV2/ResultsV2ViewSidebar.vue";
import ResultsV2ViewHeader from "@/components/Survey/ResultsV2/ResultsV2ViewHeader.vue";
import ResultsV2PrintCover from "@/components/Survey/ResultsV2/ResultsV2PrintCover.vue";
import ResultsV2CardsList from "@/components/Survey/ResultsV2/ResultsV2CardsList.vue";
import ResultsV2RightPanel from "@/components/Survey/ResultsV2/ResultsV2RightPanel.vue";
import ResultsV2Populations from "@/components/Survey/ResultsV2/ResultsV2Populations.vue";
import ResultsV2ViewSelector from "@/components/Survey/ResultsV2/ResultsV2ViewSelector.vue";
import ResultsV2GlobalOptions from "@/components/Survey/ResultsV2/ResultsV2GlobalOptions.vue";
import ResultsV2LayoutToggle from "@/components/Survey/ResultsV2/ResultsV2LayoutToggle.vue";
import CreditLimitBanner from "@/components/Survey/CreditLimitBanner.vue";

const route = useRoute();
const router = useRouter();
const store = useResultsV2Store();
const cardsStore = useResultsV2CardsStore();
const surveyStore = useSurveyStore();
const { token: surveyToken } = storeToRefs(surveyStore);
const { currentView, editMode, loading, printing } = storeToRefs(store);
const { selectedCard } = storeToRefs(cardsStore);
const { populations } = storeToRefs(useResultsV2PopulationsStore());
const { effectiveShowAllPopulation, isDefaultView } = storeToRefs(store);
const { filterSummary } = usePopulationLabels();

function describePopulation(population) {
  const group = population.search_group ?? {};
  const token = population.ephemeral ? null : group.token;
  const hasFilters = group.filters && Object.keys(group.filters).length > 0;
  const details = [
    token ? `saved · search_group_token ${token}` : "not saved, on screen only",
    hasFilters ? `filters: ${filterSummary(group.filters).replace(/\n/g, "; ")}` : "no filter, so every respondent",
  ];
  return `${group.name || "Unnamed population"} (${details.join(" · ")})`;
}

function describeDashboard(view) {
  if (!view) return null;
  if (isDefaultView.value) {
    return "Default view (not a saved dashboard, no dashboard_token: dashboard tools cannot read or edit it. Read results with get_results_map and get_aggregated_data; create a dashboard to customise it)";
  }
  return `${view.name} (dashboard_token ${view.token})`;
}

useAssistantPageState(() => ({
  Dashboard: describeDashboard(currentView.value),
  "All respondents shown": effectiveShowAllPopulation.value ? null : "no",
  Populations: populations.value.map(describePopulation),
}));

const { printDashboard, handleBeforePrint } = usePrintDashboard();

// Sync editMode → URL query (?mode=edit|view). ?layout is written by setLayout
// below, and edit mode drops it: edit is WYSIWYG for the view's saved layout.
watch(editMode, (edit) => {
  const { layout, ...rest } = route.query;
  router.replace({ query: { ...(edit ? rest : route.query), mode: edit ? "edit" : "view" } });
  if (!edit) {
    selectedCard.value = null;
    store.cancelOpenRecodeForms();
  }
});

const instance = getCurrentInstance();
const cable = instance.proxy.$cable;
function handleGlobalPointerDown(event) {
  if (!editMode.value || !selectedCard.value) return;
  // Ignore clicks inside known panels
  if (event.target.closest("[data-results-card], [data-results-sidebar], [data-results-panel], [data-results-toolbar]")) return;
  // Ignore clicks inside portaled overlays (popovers, selects, dialogs, sheets)
  // Using pointerdown so portal content is still in the DOM at this point
  if (event.target.closest("[data-reka-popper-content-wrapper], [role=dialog], [role=listbox]")) return;
  if (document.querySelector("[data-reka-popper-content-wrapper]") || document.body.style.pointerEvents === "none") return;
  selectedCard.value = null;
}

const handlePrintShortcut = (event) => {
  if (!(event.metaKey || event.ctrlKey) || event.key?.toLowerCase() !== "p") return;
  event.preventDefault();
  printDashboard();
};

onMounted(() => {
  document.addEventListener("pointerdown", handleGlobalPointerDown);
  document.addEventListener("keydown", handlePrintShortcut);
  window.addEventListener("beforeprint", handleBeforePrint);
});

onUnmounted(() => {
  document.removeEventListener("pointerdown", handleGlobalPointerDown);
  document.removeEventListener("keydown", handlePrintShortcut);
  window.removeEventListener("beforeprint", handleBeforePrint);
  cable._removeChannel("ChartsRecodesChannel", instance.uid);
  store.resetState();
});

onMounted(async () => {
  store.resetState();

  // Init edit mode from URL query (?mode=view restores view mode)
  if (route.query.mode === "view") store.setEditMode(false);

  await store.fetchViews();
  await store.fetchAvailableCards();
  store.fetchSavedPopulations().catch(() => {}); // load in background, no await needed

  const viewToken = route.params.viewToken;
  if (viewToken) {
    await store.loadView(viewToken);
  } else {
    await store.loadDefaultView();
  }

  // Subscribe to recode computation channel
  cable.subscribe({ channel: "ChartsRecodesChannel", survey_token: surveyToken.value });
  cable._addChannel(
    "ChartsRecodesChannel",
    {
      connected: () =>
        cable.perform({
          channel: "ChartsRecodesChannel",
          action: "subscribe",
          data: { survey_token: surveyToken.value },
        }),
      received: (msg) => store.recodeComputeCompleted(msg.questions),
    },
    { _uid: instance.uid, _value: instance.proxy },
  );
});

watch(
  () => route.params.viewToken,
  async (newToken) => {
    if (newToken) {
      await store.loadView(newToken);
    } else {
      await store.loadDefaultView();
    }
  },
);
</script>

<template>
  <div
    v-if="loading && !currentView"
    data-cy="survey-dashboards-page"
    class="flex h-96 items-center justify-center">
    <div class="text-muted-foreground">Loading...</div>
  </div>

  <div
    v-else
    data-cy="survey-dashboards-page"
    class="flex flex-col h-[calc(100vh-100px)] overflow-hidden print:h-auto print:overflow-visible print:[zoom:0.8] print:[print-color-adjust:exact]">
    <CreditLimitBanner class="print:hidden" />
    <!-- Toolbar row -->
    <ThreePanePage
      data-results-toolbar
      class="!h-auto mb-4 shrink-0 print:hidden"
      :span-left="editMode ? 3 : 2"
      :span-center="editMode ? 6 : 8"
      :span-right="editMode ? 3 : 2">
      <template #left>
        <div class="flex items-center gap-2">
          <ResultsV2ViewSelector class="min-w-0 flex-1" />
          <ResultsV2GlobalOptions v-if="editMode" class="shrink-0" />
        </div>
      </template>
      <template #center>
        <ResultsV2Populations />
      </template>
      <template v-if="!editMode" #right>
        <ResultsV2LayoutToggle />
      </template>
    </ThreePanePage>

    <!-- Main content row -->
    <ThreePanePage
      class="!h-auto min-h-0 flex-1 print:!block"
      :span-left="editMode ? 3 : 2"
      :span-center="editMode ? 6 : 8"
      :span-right="editMode ? 3 : 2">
      <template #left>
        <ResultsV2Sidebar v-if="editMode" />
        <ResultsV2ViewSidebar v-else class="print:hidden" />
      </template>
      <template #center>
        <ResultsV2PrintCover v-if="printing" />
        <ResultsV2ViewHeader v-if="!editMode" />
        <ResultsV2CardsList />
      </template>
      <template v-if="editMode" #right>
        <ResultsV2RightPanel />
      </template>
    </ThreePanePage>
  </div>
</template>
